import { Box, useTheme } from '@mui/material';
import React from 'react';
import { Icon } from '@phosphor-icons/react';
import { useDarkMode } from '../../../hooks/useDarkMode';
import { CustomColorPalette } from './types';

interface IconButtonProps {
    icon: Icon;
    onClick?: (e?: React.MouseEvent) => void;
    size?: number;
    color?: string;
    className?: string;
    ariaLabel?: string;
}

const getIconButtonStyles = (isDarkMode: boolean, palette: CustomColorPalette) => ({
    width: '24px',
    height: '24px',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: 'transparent',
    color: isDarkMode ? palette.gray.A100 : palette.neutral[700],
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: isDarkMode ? palette.gray[800] : palette.neutral[100],
        color: isDarkMode ? palette.gray.A100 : palette.neutral[900],
    },
    '&:active': {
        backgroundColor: isDarkMode ? palette.gray[800] : palette.neutral[100],
        color: isDarkMode ? palette.gray.A100 : palette.neutral[900],
    },
});

export const IconButton: React.FC<IconButtonProps> = ({
    icon: IconComponent,
    onClick,
    size = 16,
    color,
    className,
    ariaLabel,
}) => {
    const theme = useTheme();
    const { customColorPalette: palette } = theme;
    const isDarkMode = useDarkMode();
    const iconColor = color || (isDarkMode ? palette.gray.A100 : palette.gray[500]);

    return (
        <Box
            component="button"
            onClick={onClick}
            className={className}
            aria-label={ariaLabel}
            sx={getIconButtonStyles(isDarkMode, palette)}
        >
            <IconComponent size={size} color={iconColor} />
        </Box>
    );
};
